import React, { useState } from "react";
import { toast } from "sonner";
import { apiErrorMessage } from "./apiError";

const UNITS = ["g", "kg", "ml", "litre", "piece", "portion", "dozen", "packet", "box", "bottle", "unit"];

const componentKey = (rowId, componentRef) => `${rowId}:${componentRef}`;

function ComponentQuantityEditor({ row, item, component, api, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [quantity, setQuantity] = useState(component?.quantity ?? "");
  const [unit, setUnit] = useState(component?.unit || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/imports/${row.job_id}/recipes/${row.id}`, { action: "update_component", component_ref: item.component_ref, quantity: quantity === "" ? null : Number(quantity), unit });
      await onRefresh(); setEditing(false);
    } catch (error) { toast.error(apiErrorMessage(error, "Could not update component quantity")); }
    finally { setSaving(false); }
  };
  if (!editing) return <><span>{component?.quantity ?? "—"} {component?.unit || "—"}</span><button type="button" className="text-btn" onClick={() => setEditing(true)}>Edit</button></>;
  return <div className="recipe-import-component-quantity"><input aria-label={`Quantity for ${item.name}`} type="number" min="0.001" step="0.001" value={quantity} onChange={event => setQuantity(event.target.value)}/><select aria-label={`Unit for ${item.name}`} value={unit} onChange={event => setUnit(event.target.value)}><option value="">Choose unit…</option>{UNITS.map(value => <option key={value} value={value}>{value}</option>)}</select><button type="button" className="text-btn" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button><button type="button" className="text-btn" onClick={() => { setQuantity(component?.quantity ?? ""); setUnit(component?.unit || ""); setEditing(false); }}>Cancel</button></div>;
}

function ComponentResolution({ row, item, api, onRefresh }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ ingredients: [], prep_items: [] });
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const match = item.match || {};
  const resolved = match.state === "matched_ingredient" || match.state === "matched_prep";

  const update = async payload => {
    try {
      await api.patch(`/imports/${row.job_id}/recipes/${row.id}`, { component_ref: item.component_ref, ...payload });
      await onRefresh();
      setSearchOpen(false);
      setCreating(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not update this recipe component"));
    }
  };

  const search = async nextQuery => {
    setQuery(nextQuery);
    if (!nextQuery.trim()) {
      setResults({ ingredients: [], prep_items: [] });
      return;
    }
    try {
      const { data } = await api.get(`/imports/${row.job_id}/recipe-components/search`, { params: { q: nextQuery } });
      setResults(data);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not search components"));
    }
  };

  const createIngredient = async event => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    try {
      await update({
        action: "create_ingredient",
        ingredient: {
          ingredient_name: item.name,
          category: "Other",
          purchase_unit: form.get("purchase_unit"),
          purchase_quantity: Number(form.get("purchase_quantity")),
          purchase_price: Number(form.get("purchase_price")),
        },
      });
      toast.success(`${item.name} added to Ingredient Master`);
    } finally {
      setSaving(false);
    }
  };

  if (resolved) {
    const source = match.state === "matched_prep"
      ? (match.prep_status === "Approved" ? "Approved Prep Item" : "Prep Item · Trial")
      : "Ingredient Master";
    return <div className="recipe-import-resolution" data-testid={`recipe-component-resolved-${item.component_ref}`}>
      <strong>{match.label}</strong><small>{source}</small>
      <div className="recipe-import-resolution-actions">
        <button type="button" className="text-btn" onClick={() => { setSearchOpen(true); setCreating(false); }}>Change</button>
        <button type="button" className="text-btn" onClick={() => update({ action: "clear_match" })}>Remove match</button>
      </div>
      {searchOpen && <SearchPanel query={query} onQuery={search} results={results} onPick={(action, target_id) => update({ action, target_id })} onCancel={() => setSearchOpen(false)}/>}
    </div>;
  }

  return <div className="recipe-import-resolution" data-testid={`recipe-component-unresolved-${item.component_ref}`}>
    <strong className="red-text">Needs review</strong>
    {match.candidates?.map(candidate => <button type="button" className="text-btn" key={`${candidate.type}-${candidate.id}`} onClick={() => update({ action: candidate.type === "prep_recipe" ? "match_prep" : "match_ingredient", target_id: candidate.id })}>Match {candidate.label}</button>)}
    {!searchOpen && !creating && <div className="recipe-import-resolution-actions">
      <button type="button" className="text-btn" onClick={() => { setSearchOpen(true); setCreating(false); setQuery(""); setResults({ ingredients: [], prep_items: [] }); }}>Search Ingredient / Prep Item</button>
      <button type="button" className="text-btn" onClick={() => { setCreating(true); setSearchOpen(false); }}>Create new ingredient</button>
      <button type="button" className="text-btn" onClick={() => update({ action: "create_prep_item" })}>Create new Prep Item</button>
    </div>}
    {searchOpen && <SearchPanel query={query} onQuery={search} results={results} onPick={(action, target_id) => update({ action, target_id })} onCancel={() => setSearchOpen(false)}/>}
    {creating && <form className="recipe-import-create-form" onSubmit={createIngredient}>
      <strong>Create Ingredient Master</strong>
      <small>{item.name}</small>
      <label>Purchase unit<select name="purchase_unit" defaultValue="" required><option value="" disabled>Choose…</option>{UNITS.map(unit => <option key={unit} value={unit}>{unit}</option>)}</select></label>
      <label>Purchase quantity<input name="purchase_quantity" type="number" min="0.001" step="0.001" required/></label>
      <label>Purchase price<input name="purchase_price" type="number" min="0" step="0.01" required/></label>
      <div className="recipe-import-resolution-actions"><button type="button" className="text-btn" onClick={() => setCreating(false)}>Cancel</button><button className="primary-btn" disabled={saving}>{saving ? "Creating…" : "Create and use"}</button></div>
    </form>}
  </div>;
}

function SearchPanel({ query, onQuery, results, onPick, onCancel }) {
  return <div className="recipe-import-component-search" data-testid="recipe-component-search">
    <input aria-label="Search Ingredient or Prep Item" value={query} onChange={event => onQuery(event.target.value)} placeholder="Type to search…" autoFocus/>
    {!query.trim() && <small>Start typing to search Ingredient Master and active Prep Items.</small>}
    {query.trim() && !results.ingredients.length && !results.prep_items.length && <small>No matching Ingredient Master or active Prep Item.</small>}
    {results.ingredients.length > 0 && <div><strong>INGREDIENTS</strong>{results.ingredients.map(candidate => <button type="button" className="text-btn" key={`ingredient-${candidate.id}`} onClick={() => onPick("match_ingredient", candidate.id)}>{candidate.label}</button>)}</div>}
    {results.prep_items.length > 0 && <div><strong>PREP ITEMS</strong>{results.prep_items.map(candidate => <button type="button" className="text-btn" key={`prep-${candidate.id}`} onClick={() => onPick("match_prep", candidate.id)}>{candidate.label}</button>)}</div>}
    <button type="button" className="text-btn" onClick={onCancel}>Cancel search</button>
  </div>;
}

export default function RecipeImportResolutionReview({ job, api, committing, onStartAnother, onRefresh, onCommit }) {
  const [open, setOpen] = useState(null);
  return <section data-testid="recipe-import-review"><div className="section-head"><div><p className="eyebrow">STEP 2 / RECIPE REVIEW / {job.filename}</p><h2>{job.recipe_count || job.rows.length} recipes</h2><p>Valid recipes import as Trial. Missing output, shelf life, or PADI remains Trial readiness—not an import failure.</p></div><button className="secondary-btn" onClick={onStartAnother}>Start another import</button></div><div className="recipe-import-list">{job.rows.map(row => {
    const unresolved = row.component_reviews?.filter(item => item.match?.state === "needs_review") || [];
    const blocked = row.status === "blocked" || row.structural_errors?.length;
    return <article className="recipe-import-card" key={row.id}><button className="recipe-import-summary" onClick={() => setOpen(open === row.id ? null : row.id)}><span><strong>{row.normalized?.recipe_name || "Unnamed recipe"}</strong><small>{row.normalized?.components?.length || 0} components · {unresolved.length ? `${unresolved.length} needs review` : blocked ? "Structural blocker" : "Ready to import as Trial"}</small></span><span className={blocked || unresolved.length ? "red-text" : "green-text"}>{blocked || unresolved.length ? "Needs review" : "Trial"}</span></button>{open === row.id && <div className="recipe-import-details">{row.structural_errors?.map(error => <p className="red-text" key={error}>{error}</p>)}<table><thead><tr><th>Component</th><th>Quantity</th><th>Match</th></tr></thead><tbody>{row.component_reviews?.map(item => { const component = row.normalized?.components?.find(candidate => candidate.component_ref === item.component_ref); return <tr key={item.component_ref}><td>{item.name}</td><td><ComponentQuantityEditor row={row} item={item} component={component} api={api} onRefresh={onRefresh}/></td><td><ComponentResolution row={row} item={item} api={api} onRefresh={onRefresh}/></td></tr>; })}</tbody></table></div>}</article>;
  })}</div><div className="form-actions"><button data-testid="recipe-import-commit-button" className="primary-btn" disabled={committing} onClick={onCommit}>{committing ? "Importing…" : "Confirm & import ready recipes"}</button></div></section>;
}
